/**
 * Family Plan Management Screen (Container Component)
 *
 * Allows family plan administrators to:
 * - View family plan details (tier, quota, member count)
 * - See list of current members
 * - Invite new members via email
 * - Remove members (admin only)
 * - View and cancel pending invitations
 *
 * For regular members:
 * - View family plan details
 * - See list of family members
 * - Leave family plan
 *
 * Navigation:
 * - Accessed from Settings screen (if user is family member)
 * - Deep link: dramadetector://family/manage
 *
 * Privacy-First Design:
 * - Members can only see names/emails, not analysis content
 * - Admin cannot access individual member data
 * - Quota usage shown as aggregate only
 *
 * Architecture:
 * - Container component: Manages state and React Query hooks
 * - Business logic: useFamilyManageHandlers hook
 * - Presentation: 5 child components (FamilyPlanOverview, InviteInputForm, etc.)
 */
import React from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { Stack } from 'expo-router';
import { useTabScreenPadding } from '../../components/layout/TabScreenWrapper';
import { useFamilyPlan } from '../../hooks/useFamilyPlan';
import { useFamilyManageHandlers } from '../../hooks/useFamilyManageHandlers';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent } from '@/lib/analytics/events';
import { FamilyPlanOverview } from '../../components/screens/family-manage/FamilyPlanOverview';
import { InviteInputForm } from '../../components/screens/family-manage/InviteInputForm';
import { PendingInvitesList } from '../../components/screens/family-manage/PendingInvitesList';
import { FamilyMembersList } from '../../components/screens/family-manage/FamilyMembersList';
import { PrivacyNotice } from '../../components/screens/family-manage/PrivacyNotice';

export default function FamilyManageScreen() {
  const { t, i18n } = useTranslation('subscription');
  const locale = i18n.language;
  const { t: tCommon } = useTranslation('common'); // For common.monthly, common.remove, common.back, common.yearly
  const bottomPadding = useTabScreenPadding();
  const {
    familyPlan,
    isLoading,
    isFamilyMember,
    isFamilyAdmin,
    members,
    membersLoading,
    pendingInvites,
    invitesLoading,
    inviteMember,
    removeMember,
    cancelInvite,
    isInviting,
    isRemoving,
  } = useFamilyPlan();

  const {
    inviteEmail,
    setInviteEmail,
    showInviteInput,
    setShowInviteInput,
    handleInviteMember,
    handleRemoveMember,
    handleCancelInvite,
  } = useFamilyManageHandlers({
    familyPlan,
    members,
    inviteMember,
    removeMember,
    cancelInvite,
    t,
  });

  // Track screen view
  React.useEffect(() => {
    if (familyPlan) {
      trackEvent('Screen_View_FamilyManage', {
        is_admin: isFamilyAdmin,
        member_count: familyPlan.member_count || 0,
        pending_invites: pendingInvites.length,
        tier: familyPlan.tier as 'drama_plus' | 'dramax',
      });
    }
  }, [isFamilyAdmin, familyPlan, pendingInvites]);

  // Loading state
  if (isLoading || membersLoading) {
    return (
      <View className="flex-1 bg-gray-50 items-center justify-center">
        <Stack.Screen options={{ title: t('family.title') }} />
        <ActivityIndicator size="large" color="#14B8A6" />
      </View>
    );
  }

  // Not a family member
  if (!isFamilyMember || !familyPlan) {
    return (
      <View className="flex-1 bg-gray-50 p-6">
        <Stack.Screen options={{ title: t('family.title') }} />
        <View className="bg-white rounded-2xl p-6 shadow-sm">
          <Text className="text-xl font-semibold text-gray-900 mb-2">
            {t('family.notMember')}
          </Text>
          <Text className="text-gray-600">
            {t('family.notMemberDescription')}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <ScrollView className="flex-1 bg-gray-50" contentContainerStyle={{ paddingBottom: bottomPadding }}>
      <Stack.Screen
        options={{
          title: t('family.title'),
          headerBackTitle: tCommon('back'),
        }}
      />

      <FamilyPlanOverview
        tier={familyPlan.tier}
        billingInterval={familyPlan.billing_interval}
        dailyQuota={familyPlan.daily_quota}
        memberCount={familyPlan.member_count}
        maxMembers={familyPlan.max_members}
        isFamilyAdmin={isFamilyAdmin}
        onInvitePress={() => setShowInviteInput(!showInviteInput)}
        t={t}
        tCommon={tCommon}
      />

      {isFamilyAdmin && showInviteInput && (
        <InviteInputForm
          inviteEmail={inviteEmail}
          onChangeEmail={setInviteEmail}
          onCancel={() => setShowInviteInput(false)}
          onSend={handleInviteMember}
          isInviting={isInviting}
          t={t}
        />
      )}

      {isFamilyAdmin && (
        <PendingInvitesList
          invites={pendingInvites}
          onCancelInvite={handleCancelInvite}
          locale={locale}
          t={t}
        />
      )}

      <FamilyMembersList
        members={members}
        isFamilyAdmin={isFamilyAdmin}
        isRemoving={isRemoving}
        onRemoveMember={handleRemoveMember}
        locale={locale}
        t={t}
        tCommon={tCommon}
      />

      <PrivacyNotice t={t} />
    </ScrollView>
  );
}
